import React, { useMemo } from 'react'
import { GetOptionLabel, GetOptionValue, Options, SingleValue } from 'react-select'
import { BasicSelect, BasicSelectProps } from '../BasicSelect/BasicSelect'

export type SimpleSelectOption = {
  id: unknown
  title: string | React.ReactNode
}

export type SimpleSelectProps<Option extends SimpleSelectOption = SimpleSelectOption> = Omit<
  BasicSelectProps<Option, false, any>,
  'options' | 'value' | 'onChange' | 'getOptionValue' | 'getOptionLabel'
> & {
  options: Options<Option>
  value?: unknown
  onChange?(value?: string | number, label?: string, original?: SingleValue<Option>): void
  getOptionValue?: GetOptionValue<Option>
  getOptionLabel?: GetOptionLabel<Option>
}

/**
 * Same as BasicSelect but:
 * 1. Value is a primitive value instead of an object
 * 2. Full object value to pass to the BasicSelect is searched through the passed options based on the id field
 * 3. You don't need to pass getOptionValue and getOptionLabel because option type must contain id and title fields.
 */
export const SimpleSelect = <Option extends SimpleSelectOption = SimpleSelectOption>({
  options = [],
  value: _value,
  //@ts-expect-error for good it is necessary that only string be, but errors have already been made
  //in the select and now you can do it normally only by breaking the places where in the title jsx elements,
  //so let's just ignore errors
  getOptionLabel = option => option.title,
  getOptionValue = option => `${option.id}`,
  onChange,
  ...props
}: SimpleSelectProps<Option>) => {
  const value = useMemo(() => options.find(opt => getOptionValue(opt) === _value), [_value, getOptionValue, options])
  return (
    <BasicSelect<Option, false, any>
      value={value}
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      onChange={opt => onChange?.(opt ? getOptionValue(opt) : undefined, opt ? getOptionLabel(opt) : undefined, opt)}
      {...props}
    />
  )
}
